import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { getUploadQuota } from '../services/api'
import { initBetaMode, initBetaLimits } from '../utils/betaLimits'

export interface AppConfig {
  apiBaseUrl?: string
  aiApiBaseUrl?: string
  apiKey?: string
  encryptedKey?: string
  userEmail?: string
  userId?: number
  tenantId?: string
}

interface AppContextType {
  config: AppConfig
  betaModeEnabled: boolean
}

const AppContext = createContext<AppContextType | null>(null)

export function useAppConfig() {
  const context = useContext(AppContext)
  if (!context) {
    throw new Error('useAppConfig must be used within AppProvider')
  }
  return context.config
}

/**
 * Retorna a flag de modo BETA vinda do backend.
 * Reativo: componentes re-renderizam quando o valor chega do servidor.
 * Default false até a resposta da API ser recebida.
 */
export function useBetaMode(): boolean {
  const context = useContext(AppContext)
  if (!context) {
    throw new Error('useBetaMode must be used within AppProvider')
  }
  return context.betaModeEnabled
}

interface AppProviderProps {
  config?: AppConfig
  children: ReactNode
}

export function AppProvider({ config = {}, children }: AppProviderProps) {
  const [betaModeEnabled, setBetaModeEnabled] = useState(false)

  // Carrega a flag de modo BETA do backend assim que a aplicação monta.
  // AppProvider é pai de toda a árvore — quando o estado muda, todos os
  // consumidores de `useBetaMode()` re-renderizam com o valor correto.
  // `initBetaMode()` também é chamado para sincronizar funções utilitárias puras.
  useEffect(() => {
    getUploadQuota()
      .then(q => {
        setBetaModeEnabled(q.betaModeEnabled)
        initBetaMode(q.betaModeEnabled)
        initBetaLimits({
          maxFileSizeBytes: q.maxFileBytes,
          maxConcurrentUploads: q.maxConcurrentUploads,
          maxSummaryRegenerations: q.maxSummaryRegenerations,
          comparisonEnabled: q.comparisonEnabled,
        })
      })
      .catch(() => { /* default false — nenhuma restrição beta */ })
  }, [])

  return (
    <AppContext.Provider value={{ config, betaModeEnabled }}>
      {children}
    </AppContext.Provider>
  )
}

export default AppContext
